import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom';
import { Plane, TrainFront, CarFront, MapPin, Clock, IndianRupee, ChevronRight, Search } from 'lucide-react';
import { CORRIDORS } from '../data/corridors';
import './HowToReach.css';

const iconFor = (t) =>
    t === 'flight' ? <Plane size={22} /> : t === 'train' ? <TrainFront size={22} /> : <CarFront size={22} />;

const HowToReach = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const corridor = CORRIDORS[slug];

    // Preset to the next weekend: the upcoming Saturday, at least 2 days out
    // (so a Friday visit books next Saturday, not tomorrow's rush)
    const defaultDate = useMemo(() => {
        const d = new Date();
        let daysUntilSat = (6 - d.getDay() + 7) % 7;
        if (daysUntilSat < 2) daysUntilSat += 7;
        d.setDate(d.getDate() + daysUntilSat);
        return d.toISOString().split('T')[0];
    }, []);
    const [travelDate, setTravelDate] = useState(defaultDate);
    // Other corridors reaching the same destination (auto cross-links)
    const siblings = useMemo(() => {
        if (!corridor) return [];
        return Object.entries(CORRIDORS).filter(
            ([s, c]) => s !== slug && c.to.name === corridor.to.name
        );
    }, [slug, corridor]);

    // Deep link into the live search results, prefilled for this corridor
    // Matches /search-form's expected param shape exactly, e.g.:
    // /search-form?from=Mumbai&scode=&stype=&to=Munnar&dcode=&dtype=google&date=...
    //   &adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight
    //   &nonstop=0&slat=..&slng=..&dlat=..&dlng=..
    // dtype=google tells SearchForm to trust raw lat/lng (Munnar has no station code).
    const searchUrl = (modes = 'All') => {
        if (!corridor) return '/';
        const p = new URLSearchParams({
            from: corridor.from.name,
            scode: corridor.from.code || '',
            stype: corridor.from.type || '',
            to: corridor.to.name,
            dcode: corridor.to.code || '',
            dtype: corridor.to.type || '',
            date: travelDate,
            adults: 1,
            children: 0,
            infants: 0,
            seatClasses: 'Economy,3A',
            modes,
            nonstop: 0,
        });
        if (!corridor.from.code) {
            p.set('slat', corridor.from.lat);
            p.set('slng', corridor.from.lng);
        }
        if (!corridor.to.code) {
            p.set('dlat', corridor.to.lat);
            p.set('dlng', corridor.to.lng);
        }
        return `/search-form?${p.toString()}`;
    };

    // Per-page SEO tags + JSON-LD without extra dependencies
    useEffect(() => {
        if (!corridor) return;
        document.title = `${corridor.title} | RouteChef`;

        const ensureMeta = (attr, key, content) => {
            let el = document.head.querySelector(`meta[${attr}="${key}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
            return el;
        };
        ensureMeta('name', 'description', corridor.metaDescription);
        ensureMeta('property', 'og:title', `${corridor.title} | RouteChef`);
        ensureMeta('property', 'og:description', corridor.metaDescription);

        let canonical = document.head.querySelector('link[rel="canonical"]');
        const hadCanonical = !!canonical;
        const prevCanonical = canonical?.href;
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.href = `https://routechef.com/how-to-reach/${slug}`;

        const ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.id = 'howtoreach-jsonld';
        ld.text = JSON.stringify([
            {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: corridor.faqs.map((f) => ({
                    '@type': 'Question',
                    name: f.q,
                    acceptedAnswer: { '@type': 'Answer', text: f.a },
                })),
            },
            {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://routechef.com/' },
                    { '@type': 'ListItem', position: 2, name: 'How to Reach', item: 'https://routechef.com/how-to-reach' },
                    { '@type': 'ListItem', position: 3, name: `${corridor.from.name} to ${corridor.to.name}`, item: `https://routechef.com/how-to-reach/${slug}` },
                ],
            },
        ]);
        document.head.appendChild(ld);

        return () => {
            document.getElementById('howtoreach-jsonld')?.remove();
            if (!hadCanonical) canonical.remove();
            else if (prevCanonical) canonical.href = prevCanonical;
        };
    }, [slug, corridor]);

    if (!corridor) return <Navigate to="/" replace />;

    const { from, to } = corridor;

    return (
        <div className="htr-page">
            {/* ── Hero ── */}
            <header className="htr-hero">
                <nav className="htr-breadcrumb" aria-label="Breadcrumb">
                    <Link to="/">Home</Link>
                    <ChevronRight size={14} />
                    <Link to="/how-to-reach">How to Reach</Link>
                    <ChevronRight size={14} />
                    <span className="htr-crumb-current">{from.name} to {to.name}</span>
                </nav>
                <h1>How to Reach {to.name} from {from.name}</h1>
                <p className="htr-subtitle">
                    Flights, direct trains and cab connections — compared with real distances, times and live fares.
                </p>

                {/* Featured-snippet target: the direct answer */}
                <div className="htr-quick-answer">
                    <span className="htr-qa-label">Quick answer</span>
                    <p>{corridor.quickAnswer}</p>
                </div>
            </header>

            <main className="htr-content">
                {/* ── Live search widget ── */}
                <section className="htr-search-card" aria-label="Search live routes">
                    <div className="htr-search-route">
                        <MapPin size={18} />
                        <span><strong>{from.name}</strong> → <strong>{to.name}</strong></span>
                    </div>
                    <div className="htr-search-controls">
                        <input
                            type="date"
                            className="htr-date-input"
                            value={travelDate}
                            min={new Date().toISOString().split('T')[0]}
                            onChange={(e) => setTravelDate(e.target.value)}
                            aria-label="Travel date"
                        />
                        <button className="htr-search-btn" onClick={() => navigate(searchUrl('All'))}>
                            <Search size={17} />
                            Search live routes
                        </button>
                    </div>
                    <p className="htr-search-note">
                        Live trains, flights and mixed routes with seat availability & cab fares — door to door.
                    </p>
                </section>

                {/* ── Intro ── */}
                <section className="htr-section">
                    {corridor.intro.map((p, i) => (
                        <p key={i} className="htr-para">{p}</p>
                    ))}
                </section>

                {/* ── Distance table (snippet bait for "nearest airport/station") ── */}
                <section className="htr-section">
                    <h2>Nearest airport & railway stations to {to.name}</h2>
                    <div className="htr-table-wrap">
                        <table className="htr-table">
                            <thead>
                                <tr>
                                    <th>Gateway</th>
                                    <th>Type</th>
                                    <th>Distance to {to.name}</th>
                                    <th>Cab time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {corridor.gateways.map((g) => (
                                    <tr key={g.name}>
                                        <td>{g.name}</td>
                                        <td>{g.type}</td>
                                        <td><strong>{g.distanceKm} km</strong></td>
                                        <td>{g.cabTime}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* ── Route map (static image — evergreen-safe, image-SEO asset) ── */}
                {corridor.mapImage && (
                    <section className="htr-section">
                        <h2>{from.name} to {to.name} route map</h2>
                        <figure className="htr-map-figure">
                            <img
                                src={corridor.mapImage}
                                alt={`${from.name} to ${to.name} route map showing ${corridor.mapImageAlt || 'flight, train and cab legs'}`}
                                loading="lazy"
                            />
                            {corridor.mapImageCaption && <figcaption>{corridor.mapImageCaption}</figcaption>}
                        </figure>
                    </section>
                )}

                {/* ── Option cards ── */}
                <section className="htr-section">
                    <h2>All the ways to get there</h2>
                    <div className="htr-options">
                        {corridor.options.map((opt) => (
                            <article key={opt.id} className="htr-option-card">
                                <div className="htr-option-head">
                                    <div className="htr-option-icon">{iconFor(opt.icon)}</div>
                                    <div className="htr-option-titles">
                                        <h3>{opt.title}</h3>
                                        <span className="htr-option-badge">{opt.badge}</span>
                                    </div>
                                </div>
                                <div className="htr-option-meta">
                                    <span><Clock size={14} /> {opt.duration}</span>
                                    <span><IndianRupee size={14} /> {opt.fareNote}</span>
                                </div>
                                <p className="htr-para">{opt.body}</p>
                                {opt.mapImage && (
                                    <figure className="htr-map-figure option-map">
                                        <img
                                            src={opt.mapImage}
                                            alt={opt.mapImageAlt || `${from.name} to ${to.name} — ${opt.title} route map`}
                                            loading="lazy"
                                        />
                                    </figure>
                                )}
                                <button className="htr-option-cta" onClick={() => navigate(searchUrl(opt.modesParam))}>
                                    See live options <ChevronRight size={16} />
                                </button>
                            </article>
                        ))}
                    </div>
                </section>

                {/* ── Step-by-step for the recommended route ── */}
                <section className="htr-section">
                    <h2>Step-by-step: the recommended route</h2>
                    <ol className="htr-steps">
                        {corridor.steps.map((s, i) => (
                            <li key={i}>
                                <span className="htr-step-num">{i + 1}</span>
                                <p>{s}</p>
                            </li>
                        ))}
                    </ol>
                </section>

                {/* ── FAQ ── */}
                <section className="htr-section">
                    <h2>Frequently asked questions</h2>
                    <div className="htr-faqs">
                        {corridor.faqs.map((f, i) => (
                            <details key={i} className="htr-faq" open={i === 0}>
                                <summary>{f.q}</summary>
                                <p>{f.a}</p>
                            </details>
                        ))}
                    </div>
                </section>

                {/* ── Same-destination siblings (auto cross-links from config) ── */}
                {siblings.length > 0 && (
                    <section className="htr-section" aria-label={`Other guides for reaching ${to.name}`}>
                        <h2>Also reaching {to.name}</h2>
                        <div className="htr-sibling-links">
                            {siblings.map(([s, c]) => (
                                <Link key={s} to={`/how-to-reach/${s}`} className="htr-sibling-chip">
                                    From {c.from.name} <ChevronRight size={15} />
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* ── Bottom CTA ── */}
                <section className="htr-final-cta">
                    <h2>Ready to plan {from.name} → {to.name}?</h2>
                    <p>Compare every train, flight and cab combination in one search — with confirmed-seat routing.</p>
                    <button className="htr-search-btn large" onClick={() => navigate(searchUrl('All'))}>
                        <Search size={18} />
                        Search {from.name} to {to.name}
                    </button>
                    <div>
                        <Link to="/how-to-reach" className="htr-guides-link">
                            Browse all How-to-Reach guides <ChevronRight size={15} />
                        </Link>
                    </div>
                </section>

                <p className="htr-disclaimer">
                    Distances are road distances; journey times and fares are indicative and vary by season, traffic and availability.
                    Live fares shown in search results. Last updated July 2026.
                </p>
            </main>
        </div>
    );
};

export default HowToReach;
